import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import { action, computed, makeObservable, observable } from 'mobx'
import { getValue, isPending, trackedAction } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { UUID } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { CheckPickerItemTypeNew, OptionProps, OptionsProps } from '@avantstay/backoffice-core'

import { arriere } from '@arriere/index'
import { getPropertiesFromRegions } from '@arriere/modules/regions'

export const AllRegions = {
  label: 'All Markets',
  value: null,
}

type Region = Awaited<ReturnType<typeof arriere.regions.getRegions>>['results'][0]

class _RegionsStore {
  private selectedMarkets?: string[]
  loadedRegionsOptions: OptionProps[] = []

  regionsCache?: Region[]

  constructor() {
    this.loadRegionsOptions = trackedAction<any>(this.loadRegionsOptions.bind(this))
    makeObservable<_RegionsStore, 'selectedMarkets' | 'propertiesList'>(this, {
      selectedMarkets: observable,
      loadedRegionsOptions: observable,
      regionsCache: observable,
      regions: computed({ keepAlive: true }),
      regionsResults: computed,
      regionsOptions: computed,
      regionsOptionsNew: computed,
      checkPickerOptions: computed,
      regionsCheckPickerOptions: computed,
      isLoadingRegionOptions: computed,
      regionsById: computed,
      selectedRegionsIds: computed,
      propertiesList: computed,
      propertiesOptions: computed,
      isLoadingProperties: computed,
      defaultRegion: computed,
      setSelectedMarkets: action.bound,
    })
  }

  get regions() {
    if (!this.regionsCache?.length) {
      return arriere.regions.getRegions().then(data => {
        const orderedRegions = orderBy([...data.results], ['name'], ['asc'])

        this.regionsCache = orderedRegions

        return orderedRegions
      })
    }

    return Promise.resolve(this.regionsCache)
  }

  get regionsResults() {
    return this.regions.then(regions =>
      regions.map(region => ({
        value: region.id,
        label: region.name,
      })),
    )
  }

  get regionsOptions(): Promise<OptionProps[]> {
    return this.regions.then(regions => [
      AllRegions as unknown as OptionProps,
      ...regions.map(it => ({
        value: it.id,
        label: it.name,
      })),
    ])
  }

  get regionsOptionsNew(): CheckPickerItemTypeNew<string>[] {
    return getValue(this.regionsResults)
  }

  /**
   * @deprecated use regionOptions instead
   */
  get checkPickerOptions(): Promise<OptionsProps<string>[]> {
    return this.regions.then(regions => [
      ...regions.map(
        it =>
          ({
            value: it.id,
            label: it.name,
          }) as OptionsProps<string>,
      ),
    ])
  }

  get regionsCheckPickerOptions(): OptionsProps<string>[] {
    return getValue(this.checkPickerOptions)
  }

  get isLoadingRegionOptions(): boolean {
    return isPending(this.regions)
  }

  async loadRegionsOptions() {
    this.regions.then(regions => {
      const result = [
        AllRegions,
        ...regions.map(it => ({
          value: it.id,
          label: it.name,
        })),
      ]
      this.loadedRegionsOptions = result as OptionProps[]
    })
  }

  get regionsById() {
    return this.regionsCache?.reduce<Record<UUID, Region>>((regions, currentRegion) => {
      regions[currentRegion.id] = currentRegion
      return regions
    }, {})
  }

  get selectedRegionsIds() {
    return this.selectedMarkets
  }

  private get propertiesList(): Promise<CheckPickerItemTypeNew<string>[]> {
    if (!!this.selectedRegionsIds?.length) {
      return getPropertiesFromRegions(this.selectedRegionsIds).then(data => {
        const groupedList = groupBy(data.results, 'region.name')

        return data?.results?.map(property => ({
          label: property.name,
          value: property.id,
          groupBy: property.region
            ? `${property.region.name} • ${groupedList[property.region.name].length}`
            : undefined,
        }))
      })
    }

    return Promise.resolve([])
  }

  get propertiesOptions() {
    return getValue(this.propertiesList)
  }

  get isLoadingProperties() {
    return isPending(this.propertiesList)
  }

  mapIdsToNames = computedFn(
    (ids: UUID[]): string[] =>
      ids.map(id => this.regionsById?.[id]?.name).filter(Boolean) as string[],
  )

  get defaultRegion() {
    return getValue(this.regions)?.[0]
  }

  getRegionById = (id: UUID) => {
    return this.regionsById?.[id]
  }

  setSelectedMarkets(marketsIds?: string[]) {
    this.selectedMarkets = marketsIds
  }
}

export const RegionsStore = new _RegionsStore()
